import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Mail, Send, Clock, CheckCircle2, XCircle, CalendarClock, Lock, Settings2, KeyRound,
} from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/date";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ColumnOrderList } from "@/components/ColumnOrderList";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function Reminders() {
  const [preview, setPreview] = useState(null);
  const [logs, setLogs] = useState(null);
  const [settings, setSettings] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendPw, setSendPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [savePw, setSavePw] = useState("");
  const [pwDialog, setPwDialog] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });

  const load = async () => {
    const [p, l, s] = await Promise.all([
      api.get("/reminders/preview"), api.get("/reminders/logs"), api.get("/settings/reminder"),
    ]);
    setPreview(p.data.recipients);
    setLogs(l.data);
    setSettings(s.data);
  };
  useEffect(() => { load(); }, []);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reminders/send-now", { password: sendPw });
      toast.success(`Sent to ${data.customers_notified} customers${data.failed ? `, ${data.failed} failed` : ""}`);
      setSendOpen(false); setSendPw(""); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send reminders");
    } finally {
      setSending(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/reminder", {
        password: savePw,
        schedule: settings.schedule,
        template: settings.template,
        columns_domestic: settings.columns_domestic,
        columns_export: settings.columns_export,
      });
      setSettings(data); setSavePw("");
      toast.success("Reminder settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async () => {
    try {
      await api.post("/settings/reminder/password", pwForm);
      toast.success("Send password updated");
      setPwDialog(false); setPwForm({ current_password: "", new_password: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update password");
    }
  };

  const setSched = (k, v) => setSettings((s) => ({ ...s, schedule: { ...s.schedule, [k]: v } }));
  const setTmpl = (k, v) => setSettings((s) => ({ ...s, template: { ...s.template, [k]: v } }));
  const setCols = (which, list) => setSettings((s) => ({ ...s, [which]: list }));

  const needsPw = settings?.has_password;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Email Reminders</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Automatic reminders to overdue customers. Excluded customers are always skipped.
          </p>
        </div>
        <Dialog open={sendOpen} onOpenChange={setSendOpen}>
          <DialogTrigger asChild>
            <Button disabled={!preview?.length} data-testid="send-batch-reminder-button">
              <Send className="w-4 h-4 mr-2" /> Send reminders now
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send reminders to {preview?.length} customers?</DialogTitle>
              <DialogDescription>
                Each customer receives one email summarising their overdue invoices. Excluded customers and
                those without an email are skipped.
              </DialogDescription>
            </DialogHeader>
            {needsPw && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Lock className="w-3.5 h-3.5" /> Send password</Label>
                <Input type="password" value={sendPw} onChange={(e) => setSendPw(e.target.value)}
                       placeholder="Enter send password" data-testid="send-password-input" autoFocus />
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setSendOpen(false)}>Cancel</Button>
              <Button onClick={sendNow} disabled={sending || (needsPw && !sendPw)} data-testid="send-batch-confirm-button">
                {sending ? "Sending…" : "Send now"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Settings */}
      <Card className="p-5 lg:p-6 space-y-6" data-testid="reminder-settings-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-lg font-medium">Reminder controls</h3>
          </div>
          <Button variant="outline" size="sm" onClick={() => setPwDialog(true)} data-testid="manage-password-button">
            <KeyRound className="w-4 h-4 mr-2" />
            {needsPw ? "Change send password" : "Set send password"}
          </Button>
        </div>

        {!settings ? (
          <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : (
          <>
            {/* Schedule */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-center gap-3">
                <Switch checked={settings.schedule.enabled}
                        onCheckedChange={(v) => setSched("enabled", v)} data-testid="schedule-enabled-switch" />
                <Label>Auto-reminders {settings.schedule.enabled ? "on" : "off"}</Label>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Frequency</Label>
                <Select value={settings.schedule.frequency} onValueChange={(v) => setSched("frequency", v)}>
                  <SelectTrigger data-testid="schedule-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {settings.schedule.frequency === "weekly" && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Day of week</Label>
                  <Select value={String(settings.schedule.weekday)} onValueChange={(v) => setSched("weekday", Number(v))}>
                    <SelectTrigger data-testid="schedule-weekday-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {settings.schedule.frequency === "monthly" && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Day of month (1-28)</Label>
                  <Input type="number" min={1} max={28} value={settings.schedule.day_of_month}
                         onChange={(e) => setSched("day_of_month", Number(e.target.value))}
                         data-testid="schedule-dom-input" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs">Hour (UTC, 0-23)</Label>
                <Input type="number" min={0} max={23} value={settings.schedule.hour_utc}
                       onChange={(e) => setSched("hour_utc", Number(e.target.value))}
                       data-testid="schedule-hour-input" />
              </div>
            </div>

            {/* Template */}
            <div className="grid grid-cols-1 gap-4 border-t border-border/60 pt-5">
              <div className="space-y-1.5">
                <Label className="text-xs">Email subject <span className="text-muted-foreground">(placeholders: {"{customer_name}"}, {"{total}"})</span></Label>
                <Input value={settings.template.subject} onChange={(e) => setTmpl("subject", e.target.value)}
                       placeholder="Leave blank for default subject" data-testid="template-subject-input" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Intro message</Label>
                  <Textarea rows={3} value={settings.template.intro} onChange={(e) => setTmpl("intro", e.target.value)}
                            placeholder="Dear {customer_name}, your outstanding total is {total}…"
                            data-testid="template-intro-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Footer message</Label>
                  <Textarea rows={3} value={settings.template.footer} onChange={(e) => setTmpl("footer", e.target.value)}
                            placeholder="If already paid, please ignore…" data-testid="template-footer-input" />
                </div>
              </div>
            </div>

            {/* Columns */}
            <div className="border-t border-border/60 pt-5 space-y-5">
              <p className="text-sm text-muted-foreground">
                Domestic and Export customers receive separate emails with their own invoice tables. Choose the
                columns shown in each. Drag the handle to reorder — the same order is used in customer statements.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Domestic email columns</Label>
                  <ColumnOrderList prefix="domestic" available={settings.columns_available_domestic || {}}
                                   selected={settings.columns_domestic || []}
                                   onChange={(v) => setCols("columns_domestic", v)} />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Export email columns</Label>
                  <ColumnOrderList prefix="export" available={settings.columns_available_export || {}}
                                   selected={settings.columns_export || []}
                                   onChange={(v) => setCols("columns_export", v)} />
                </div>
              </div>
            </div>

            {/* Save */}
            <div className="flex items-center justify-end gap-3 border-t border-border/60 pt-5 flex-wrap">
              {needsPw && (
                <Input type="password" value={savePw} onChange={(e) => setSavePw(e.target.value)}
                       placeholder="Send password to save" className="w-56" data-testid="save-password-input" />
              )}
              <Button onClick={saveSettings} disabled={saving || (needsPw && !savePw)} data-testid="save-settings-button">
                {saving ? "Saving…" : "Save settings"}
              </Button>
            </div>
          </>
        )}
      </Card>

      <Card className="p-5 lg:p-6 flex items-start gap-4 bg-primary/5 border-primary/20">
        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <CalendarClock className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-heading font-medium">
            {settings?.schedule?.enabled ? "Automatic schedule active" : "Automatic schedule paused"}
          </h3>
          <p className="text-sm text-muted-foreground mt-1" data-testid="schedule-summary">
            {settings ? (
              settings.schedule.enabled
                ? <>Reminders run <strong>{settings.schedule.frequency}
                  {settings.schedule.frequency === "weekly" ? ` on ${WEEKDAYS[settings.schedule.weekday]}` : ""}
                  {settings.schedule.frequency === "monthly" ? ` on day ${settings.schedule.day_of_month}` : ""} at {String(settings.schedule.hour_utc).padStart(2, "0")}:00 UTC</strong>.</>
                : "Turn on auto-reminders above to schedule automatic sends."
            ) : "Loading schedule…"}
          </p>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:gap-6">
        <Card className="lg:col-span-2 p-5 lg:p-6" data-testid="reminder-recipients-card">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-lg font-medium">Next run recipients</h3>
          </div>
          {!preview ? (
            <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
          ) : preview.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No eligible overdue customers right now.</p>
          ) : (
            <div className="space-y-3 max-h-[420px] overflow-y-auto">
              {preview.map((r, i) => (
                <div key={i} className="flex items-center justify-between gap-3 pb-3 border-b border-border/60 last:border-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{r.customer_name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {r.no_email ? <span className="text-amber-600 dark:text-amber-500">no email — add in Customers</span> : `To: ${r.emails?.length ? r.emails.join(", ") : r.email}`}
                    </p>
                    {r.cc && <p className="text-[11px] text-muted-foreground truncate">CC: {r.cc}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-mono font-semibold">{r.total_display}</p>
                    <p className="text-[11px] text-muted-foreground">{r.count} invoice(s)</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="lg:col-span-3 overflow-hidden" data-testid="reminder-logs-table">
          <div className="p-5 lg:p-6 pb-3 flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-lg font-medium">Delivery log</h3>
          </div>
          {!logs ? (
            <div className="p-6 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
          ) : logs.length === 0 ? (
            <p className="text-sm text-muted-foreground p-12 text-center">No reminders sent yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead className="text-xs uppercase tracking-wider">Customer</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Sent</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">Total</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((l, i) => (
                    <TableRow key={i} className="hover:bg-muted/40">
                      <TableCell className="font-medium min-w-[150px] max-w-[240px]">
                        <span className="break-words">{l.customer_name}</span>
                        <div className="text-[11px] text-muted-foreground break-words">{l.to}</div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmtDateTime(l.sent_at)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{l.total_display}</TableCell>
                      <TableCell className="text-center">
                        {l.status === "sent" ? (
                          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> sent
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-rose-500/10 text-rose-500 border-rose-500/20">
                            <XCircle className="w-3 h-3 mr-1" /> failed
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      {/* Password dialog */}
      <Dialog open={pwDialog} onOpenChange={setPwDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{needsPw ? "Change send password" : "Set send password"}</DialogTitle>
            <DialogDescription>
              This password is required to send reminders and to change reminder settings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {needsPw && (
              <div className="space-y-1.5">
                <Label className="text-xs">Current password</Label>
                <Input type="password" value={pwForm.current_password}
                       onChange={(e) => setPwForm((f) => ({ ...f, current_password: e.target.value }))}
                       data-testid="current-password-input" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">New password (min 4 chars)</Label>
              <Input type="password" value={pwForm.new_password}
                     onChange={(e) => setPwForm((f) => ({ ...f, new_password: e.target.value }))}
                     data-testid="new-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPwDialog(false)}>Cancel</Button>
            <Button onClick={savePassword} disabled={!pwForm.new_password || (needsPw && !pwForm.current_password)} data-testid="save-password-confirm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
